import { useMemo, useRef, useState } from 'react'
import { Send, Sparkles } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import {
  stopSearchTextAnimation,
  useSearchTextTypewriter,
} from '../../hooks/useSearchTextTypewriter.js'
import { IconButton } from '../ui/IconButton.jsx'

function resolveSearchTexts(conf) {
  const raw = conf?.searchTexts
  if (!Array.isArray(raw)) return []
  return raw.map((t) => String(t ?? '').trim()).filter(Boolean)
}

/** Agent Settings → Text Entry Placeholder Text (V1 `placeholderText`). */
function resolveTextEntryPlaceholder(conf) {
  return (
    conf?.composerPlaceholder ||
    conf?.widget_placeholder_text ||
    'Need help? Ask here…'
  )
}

/**
 * Shared AI Display search bar trigger (in-page and floating). Types
 * configured `searchTexts` into the input with V1 timing when chat is
 * closed; stops on open or user interaction in interactive mode.
 */
export function SearchTrigger({
  conf,
  open = false,
  interactive = false,
  isTyping = false,
  onOpenChat,
  onToggleChat,
  onSend,
  className,
  formClassName,
  style,
}) {
  const inputRef = useRef(null)
  const [inputValue, setInputValue] = useState('')
  const [userHasInteracted, setUserHasInteracted] = useState(false)

  const searchTexts = useMemo(() => resolveSearchTexts(conf), [conf?.searchTexts])
  const textEntryPlaceholder = useMemo(
    () => resolveTextEntryPlaceholder(conf),
    [conf?.composerPlaceholder, conf?.widget_placeholder_text],
  )

  const animationEnabled = !open && !userHasInteracted

  const { stopAnimation, refs, displayPlaceholder, shouldAnimate } =
    useSearchTextTypewriter({
      searchTexts,
      staticPlaceholder: textEntryPlaceholder,
      enabled: animationEnabled,
      inputRef,
    })

  const inputStyle = useMemo(() => {
    const s = {}
    const bg = conf?.widgetInputBackgroundColor?.trim?.()
    const fg = conf?.widgetInputTextColor?.trim?.()
    if (bg) s.backgroundColor = bg
    if (fg) s.color = fg
    return Object.keys(s).length ? s : undefined
  }, [conf?.widgetInputBackgroundColor, conf?.widgetInputTextColor])

  const brand = conf?.widgetButtonColor?.trim?.() || 'var(--color-brand)'
  const brandFg =
    conf?.widgetButtonTextColor?.trim?.() || 'var(--color-brand-foreground)'

  const ariaLabel =
    searchTexts.length === 1 ? searchTexts[0] : textEntryPlaceholder

  const handleUserInteract = () => {
    if (!interactive || userHasInteracted) return
    setUserHasInteracted(true)
    stopSearchTextAnimation(refs, inputRef)
    setInputValue('')
    if (inputRef.current) {
      inputRef.current.placeholder = textEntryPlaceholder
    }
  }

  const sendFromSearch = (text) => {
    const t = String(text || '').trim()
    if (!t) return
    onOpenChat?.()
    requestAnimationFrame(() => {
      onSend?.(t)
    })
  }

  const onSearchSubmit = (e) => {
    e.preventDefault()
    if (!interactive) {
      onOpenChat?.()
      return
    }
    sendFromSearch(inputValue)
    setInputValue('')
  }

  const activateChat = (e) => {
    e?.stopPropagation?.()
    e?.preventDefault?.()
    stopAnimation()
    onOpenChat?.()
  }

  const placeholderForInput =
    interactive || !shouldAnimate
      ? displayPlaceholder
      : animationEnabled
        ? ''
        : displayPlaceholder

  const useControlledValue = interactive && userHasInteracted

  return (
    <form
      className={cn(
        'flex w-full min-w-[280px] max-w-[500px] items-center gap-1 rounded-full border border-solid border-[var(--color-border)]',
        'bg-[var(--color-surface)] px-1 py-1 shadow-sm',
        formClassName,
        className,
      )}
      style={style}
      onSubmit={onSearchSubmit}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        className="flex shrink-0 items-center justify-center rounded-full p-2 text-[color:var(--color-brand)] hover:bg-[var(--color-muted)]/30"
        style={{ color: brand }}
      >
        <Sparkles className="h-5 w-5" aria-hidden />
      </button>
      <input
        ref={inputRef}
        id={`ometrics-search-${conf?.instanceId || 'widget'}`}
        type="text"
        name="ometrics-ai-search"
        autoComplete="off"
        aria-label={ariaLabel}
        placeholder={placeholderForInput}
        style={inputStyle}
        className={cn(
          'min-w-0 flex-1 border-0 bg-transparent py-2 pl-1 pr-1 text-[15px] outline-none',
          'text-[var(--color-surface-foreground)] placeholder:text-[var(--color-muted-foreground)]',
          !interactive && 'cursor-pointer',
        )}
        {...(useControlledValue
          ? {
              value: inputValue,
              onChange: (e) => {
                handleUserInteract()
                setInputValue(e.target.value)
              },
              onKeyDown: (e) => {
                if (e.key === 'Enter' && inputValue.trim()) {
                  e.preventDefault()
                  sendFromSearch(inputValue)
                  setInputValue('')
                }
              },
              onFocus: handleUserInteract,
              onClick: (e) => {
                e.stopPropagation()
                handleUserInteract()
              },
              readOnly: false,
            }
          : interactive
            ? {
                onFocus: handleUserInteract,
                onClick: (e) => {
                  e.stopPropagation()
                  handleUserInteract()
                },
                onChange: (e) => {
                  handleUserInteract()
                  setInputValue(e.target.value)
                },
                onKeyDown: (e) => {
                  if (e.key === 'Enter' && String(e.currentTarget.value || '').trim()) {
                    e.preventDefault()
                    sendFromSearch(e.currentTarget.value)
                    setInputValue('')
                  }
                },
                readOnly: false,
              }
            : {
                readOnly: true,
                onClick: activateChat,
              })}
      />
      <IconButton
        type="button"
        aria-label={interactive ? 'Send message' : 'Open chat'}
        disabled={
          interactive ? isTyping || inputValue.trim().length === 0 : false
        }
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          if (interactive) {
            if (inputValue.trim()) {
              sendFromSearch(inputValue)
              setInputValue('')
            }
          } else {
            onToggleChat?.()
          }
        }}
        className={cn(
          'shrink-0 self-center rounded-full border-transparent shadow-sm',
          'bg-[var(--color-brand)] text-[var(--color-brand-foreground)]',
          'hover:opacity-90',
        )}
        style={{ backgroundColor: brand, color: brandFg }}
      >
        <Send className="h-4 w-4" />
      </IconButton>
    </form>
  )
}
